// Custom hooks for data fetching
import { useState, useEffect, useCallback } from 'react';
import { patientService } from '../services/patientService';
import { clinicalService } from '../services/clinicalService';
import { healthIntelligenceService } from '../services/healthIntelligenceService';
import longevityService from '../services/longevityService';
import { fileService } from '../services/fileService';

// Generic fetch hook
export const useFetch = (fetchFn, dependencies = []) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const refetch = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await fetchFn();
      if (result.success) {
        setData(result.data);
      } else {
        setError(result.error);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [fetchFn]);

  useEffect(() => {
    refetch();
  }, dependencies);

  return { data, loading, error, refetch };
};

// Patients hook
export const usePatients = (options = {}) => {
  const [patients, setPatients] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchPatients = useCallback(async (fetchOptions = options) => {
    setLoading(true);
    setError(null);
    try {
      const result = await patientService.getPatients(fetchOptions);
      if (result.success) {
        setPatients(result.data);
        setTotal(result.total);
      } else {
        setError(result.error);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPatients(options);
  }, [options.page, options.pageSize, options.search, options.status]);

  return { patients, total, loading, error, refetch: fetchPatients };
};

// Single patient hook
export const usePatient = (patientId) => {
  const [patient, setPatient] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchPatient = useCallback(async () => {
    if (!patientId) return;
    setLoading(true);
    setError(null);
    try {
      const result = await patientService.getPatient(patientId);
      if (result.success) {
        setPatient(result.data);
      } else {
        setError(result.error);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [patientId]);

  useEffect(() => {
    fetchPatient();
  }, [patientId]);

  return { patient, loading, error, refetch: fetchPatient };
};

// Patient clinical data hook
export const usePatientClinicalData = (patientId) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchData = useCallback(async () => {
    if (!patientId) return;
    setLoading(true);
    setError(null);
    try {
      const result = await clinicalService.getPatientFullData(patientId);
      if (result.success) {
        setData(result.data);
      } else {
        setError(result.error);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [patientId]);

  useEffect(() => {
    fetchData();
  }, [patientId]);

  return { data, loading, error, refetch: fetchData };
};

// Health scores hook
export const useHealthScores = (patientId) => {
  const [scores, setScores] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchScores = useCallback(async () => {
    if (!patientId) return;
    setLoading(true);
    try {
      const result = await healthIntelligenceService.getHealthScores(patientId);
      if (result.success) {
        setScores(result.data);
      } else {
        setError(result.error);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [patientId]);

  useEffect(() => {
    fetchScores();
  }, [patientId]);

  return { scores, loading, error, refetch: fetchScores };
};

// Longevity profile hook
export const useLongevityProfile = (patientId) => {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchProfile = useCallback(async () => {
    if (!patientId) return;
    setLoading(true);
    try {
      const result = await longevityService.getLongevityProfile(patientId);
      if (result.success) {
        setProfile(result.data);
      } else {
        setError(result.error);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [patientId]);

  useEffect(() => {
    fetchProfile();
  }, [patientId]);

  return { profile, loading, error, refetch: fetchProfile };
};

// Wearables hook
export const useWearables = (patientId) => {
  const [devices, setDevices] = useState([]);
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchData = useCallback(async () => {
    if (!patientId) return;
    setLoading(true);
    try {
      const [devicesResult, metricsResult] = await Promise.all([
        healthIntelligenceService.getWearableDevices(patientId),
        healthIntelligenceService.getWearableMetrics(patientId)
      ]);
      
      if (devicesResult.success) {
        setDevices(devicesResult.data?.devices || []);
      }
      if (metricsResult.success) {
        setMetrics(metricsResult.data);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [patientId]);

  useEffect(() => {
    fetchData();
  }, [patientId]);

  return { devices, metrics, loading, error, refetch: fetchData };
};

// Population health hook
export const usePopulationHealth = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [summary, demographics, conditions, stratification, careGaps, metrics] = await Promise.all([
        healthIntelligenceService.getPopulationSummary(),
        healthIntelligenceService.getPopulationDemographics(),
        healthIntelligenceService.getChronicConditions(),
        healthIntelligenceService.getRiskStratification(),
        healthIntelligenceService.getCareGaps(),
        healthIntelligenceService.getQualityMetrics()
      ]);
      
      setData({
        summary: summary.data,
        demographics: demographics.data,
        conditions: conditions.data,
        stratification: stratification.data,
        careGaps: careGaps.data,
        metrics: metrics.data
      });
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, []);

  return { data, loading, error, refetch: fetchData };
};

// Remote monitoring hook
export const useRemoteMonitoring = () => {
  const [patients, setPatients] = useState([]);
  const [alertRules, setAlertRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [patientsResult, rulesResult] = await Promise.all([
        healthIntelligenceService.getMonitoredPatients(),
        healthIntelligenceService.getAlertRules()
      ]);
      
      if (patientsResult.success) {
        setPatients(patientsResult.data?.patients || []);
      }
      if (rulesResult.success) {
        setAlertRules(rulesResult.data?.rules || []);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, []);

  return { patients, alertRules, loading, error, refetch: fetchData };
};

// Notifications hook
export const useNotifications = (unreadOnly = false) => {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchNotifications = useCallback(async () => {
    setLoading(true);
    try {
      const { getMyNotifications } = await import('@/services/notificationService');
      const result = await getMyNotifications(unreadOnly ? 'unread' : null);
      setNotifications(result?.notifications || []);
    } catch (err) {
      // Fallback to file service notifications
      try {
        const result = await fileService.getNotifications(unreadOnly);
        if (result.success) setNotifications(result.data || []);
        else setError(result.error);
      } catch (e) {
        setError(e.message);
      }
    } finally {
      setLoading(false);
    }
  }, [unreadOnly]);

  const markAsRead = async (notificationId) => {
    try {
      const { markAsRead: markRead } = await import('@/services/notificationService');
      await markRead(notificationId);
      setNotifications(prev =>
        prev.map(n => n.id === notificationId ? { ...n, read: true } : n)
      );
      return { success: true };
    } catch {
      const result = await fileService.markNotificationRead(notificationId);
      if (result.success) {
        setNotifications(prev =>
          prev.map(n => n.id === notificationId ? { ...n, read: true } : n)
        );
      }
      return result;
    }
  };

  useEffect(() => {
    fetchNotifications();
  }, [unreadOnly]);

  return { notifications, loading, error, refetch: fetchNotifications, markAsRead };
};

// Dashboard stats hook
export const useDashboardStats = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchStats = useCallback(async () => {
    setLoading(true);
    try {
      const [patientsResult, populationResult] = await Promise.all([
        patientService.getPatients({ page: 1, pageSize: 10 }),
        healthIntelligenceService.getPopulationSummary()
      ]);
      
      setStats({
        totalPatients: patientsResult.total || populationResult.data?.totalPatients || 0,
        activeMonitoring: populationResult.data?.activeMonitoring || 0,
        highRisk: populationResult.data?.highRisk || 0,
        careGaps: populationResult.data?.careGaps || 0,
        recentPatients: patientsResult.data || []
      });
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStats();
  }, []);

  return { stats, loading, error, refetch: fetchStats };
};
